import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  query,
  where,
  arrayUnion,
  addDoc,
  setDoc,
  getDoc,
  deleteDoc,
} from "firebase/firestore";
import { sendAppointmentConfirmationEmail } from "firebaseConfig";
import { firestore } from "firebaseConfig";
import moment from "moment";
import { getFormattedClientName } from "utils/helper";

//List of leads
export const getLeads = createAsyncThunk(
  "Lead/getLeads",
  async ({ uid }, thunkAPI) => {
    try {
      let data = [];
      const q = query(
        collection(firestore, "lead"),
        where("adminId", "==", uid)
      );

      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        // let timestamp = null;
        let updatedAt = null;
        // if (doc.data().createdAt) {
        //     let createdAt = doc.data().createdAt
        //     timestamp = createdAt?.nanoseconds / 1000000 + createdAt?.seconds * 1000
        //     timestamp = moment.utc(timestamp)
        // }

        if (doc.data().updatedAt) {
          updatedAt = doc.data().updatedAt;
          updatedAt =
            updatedAt?.nanoseconds / 1000000 + updatedAt?.seconds * 1000;
          updatedAt = moment.utc(updatedAt);
        }

        data.push({
          ...doc.data(),
          id: doc.id,
          leadName: doc.id,
          updatedAt,
        });
      });

      return [...data];
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  }
);

export const AddLead = createAsyncThunk(
  "Lead/AddLead",
  async ({ formdata, uid }, thunkAPI) => {
    try {
      const clientsRef = collection(firestore, "client");
      const snapshot = await getDocs(
        query(clientsRef, where("adminId", "==", uid))
      );

      const normalize = (val) =>
        String(val || "")
          .trim()
          .toLowerCase();

      const inputName = normalize(getFormattedClientName(formdata.clientName));
      const inputPhone = normalize(formdata.phoneNumber);
      const inputEmail = normalize(formdata.email);

      let isSameClientExists = false;
      let conflictFields = new Set();

      snapshot.forEach((docSnap) => {
        const data = docSnap.data();

        const dbName = normalize(getFormattedClientName(data.name));
        const dbPhone = data.phoneNumber;
        const dbEmail = normalize(data.email);

        const nameMatch = dbName === inputName;
        const phoneMatch = dbPhone === inputPhone;
        const emailMatch = dbEmail === inputEmail;

        if (nameMatch && phoneMatch && emailMatch) {
          isSameClientExists = true;
          return;
        }

        if (phoneMatch) conflictFields.add("Phone Number");
        if (emailMatch) conflictFields.add("Email");
        if (nameMatch) conflictFields.add("Name");
      });

      if (!isSameClientExists && conflictFields.size > 0) {
        const fieldList = Array.from(conflictFields).join(" and ");
        return thunkAPI.rejectWithValue({
          message: `${fieldList} already ${
            conflictFields.size > 1 ? "exist" : "exists"
          }.`,
        });
      }

      const timestamp = new Date();

      // Lead data
      const leadData = {
        clientName: getFormattedClientName(formdata.clientName),
        countryCode: formdata.countryCode,
        phoneNumber: String(formdata.phoneNumber),
        email: formdata.email,
        customerNotes: formdata.customerNotes,
        source: formdata.source.value,
        service: formdata.service.value,
        employee: formdata.employee ? formdata.employee.value : "",
        status: formdata.status.value,
        followUpDate: formdata.followUpDate,
        referredBy: formdata.referredBy ?? "",
        createdAt: timestamp,
        adminId: uid,
        ...(formdata.consultantNotestext && {
          consultantNotes: [
            { date: timestamp, consultantNote: formdata.consultantNotestext },
          ],
        }),
      };

      // Add lead
      const leadRef = await addDoc(collection(firestore, "lead"), leadData);
      const leadId = leadRef.id;

      if (!isSameClientExists) {
        let customerCount = 0;
        snapshot.forEach((docSnap) => {
          const cusId = docSnap.data().customerId;
          if (cusId && cusId > customerCount) {
            customerCount = cusId;
          }
        });

        const nextCustomerId = customerCount + 1;

        const clientData = {
          adminId: uid,
          name: getFormattedClientName(formdata.clientName),
          countryCode: formdata.countryCode,
          phoneNumber: String(formdata.phoneNumber),
          email: formdata.email,
          customerId: nextCustomerId,
        };

        await setDoc(doc(firestore, "client", leadId), clientData);
      }

      return {
        ...leadData,
        id: leadRef.id,
      };
    } catch (e) {
      return thunkAPI.rejectWithValue({
        message: e.message || "Something went wrong while adding lead.",
      });
    }
  }
);

export const mapClientIdsByPhone = async (items) => {
  const clientRef = collection(firestore, "client");
  const phoneNumbers = items
    .filter((item) => item.phoneNumber)
    .map((item) => item.phoneNumber);

  if (phoneNumbers.length === 0)
    return items.map((item) => ({ ...item, clientId: null }));

  const chunkSize = 10;
  const chunks = [];
  for (let i = 0; i < phoneNumbers.length; i += chunkSize) {
    chunks.push(phoneNumbers.slice(i, i + chunkSize));
  }

  let allClients = [];
  for (const chunk of chunks) {
    const q = query(clientRef, where("phoneNumber", "in", chunk));
    const querySnapshot = await getDocs(q);
    allClients = allClients.concat(
      querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
    );
  }

  const phoneToClientId = {};
  allClients.forEach((client) => {
    if (client.phoneNumber) phoneToClientId[client.phoneNumber] = client.id;
  });

  return items.map((item) => ({
    ...item,
    clientId: item.phoneNumber
      ? phoneToClientId[item.phoneNumber] || null
      : null,
  }));
};

export const EditLead = createAsyncThunk(
  "Lead/EditLead",
  async ({ formdata, uid }, thunkAPI) => {
    try {
      const clientCollection = collection(firestore, "client");

      // Check for existing client by phoneNumber (excluding current client ID)
      const phoneQuery = query(
        clientCollection,
        where("phoneNumber", "==", formdata.phoneNumber),
        where("adminId", "==", formdata.adminId)
      );
      const phoneSnapshot = await getDocs(phoneQuery);
      const phoneConflict = phoneSnapshot.docs.some(
        (doc) => doc.id !== formdata.clientId
      );
      if (phoneConflict) {
        return thunkAPI.rejectWithValue({
          message: "Client with this Phone Number already exists.",
        });
      }

      // Check for existing client by email (excluding current client ID)
      if (formdata.email?.trim()) {
        const emailQuery = query(
          clientCollection,
          where("email", "==", formdata.email),
          where("adminId", "==", formdata.adminId)
        );
        const emailSnapshot = await getDocs(emailQuery);
        const emailConflict = emailSnapshot.docs.some(
          (doc) => doc.id !== formdata.clientId
        );
        if (emailConflict) {
          return thunkAPI.rejectWithValue({
            message: "Client with this Email already exists.",
          });
        }
      }

      const formattedName = getFormattedClientName(formdata.clientName);
      const updateData = {
        clientName: formattedName,
        countryCode: formdata.countryCode,
        phoneNumber: String(formdata.phoneNumber),
        email: formdata.email,
        customerNotes: formdata.customerNotes,
        source: formdata.source.value,
        service: formdata.service.value,
        employee: formdata.employee.value,
        status: formdata.status.value,
        followUpDate: formdata.followUpDate,
        referredBy: formdata.referredBy ?? "",
        updatedAt: new Date(),
      };

      // Update lead
      const leadRef = doc(firestore, "lead", formdata.id);
      if (formdata.consultantNotestext) {
        await updateDoc(leadRef, {
          ...updateData,
          consultantNotes: arrayUnion({
            date: new Date(),
            consultantNote: formdata.consultantNotestext,
          }),
        });
      } else {
        await updateDoc(leadRef, updateData);
      }

      // Check if client exists
      const clientRef = doc(firestore, "client", formdata.clientId);
      const clientSnap = await getDoc(clientRef);

      const clientData = {
        name: formattedName,
        countryCode: formdata.countryCode,
        phoneNumber: String(formdata.phoneNumber),
        email: formdata.email,
        adminId: formdata.adminId,
      };

      if (clientSnap.exists()) {
        await updateDoc(clientRef, clientData);
      } else {
        await setDoc(clientRef, {
          ...clientData,
          createdAt: new Date(),
        });
      }

      return {
        id: formdata.id,
        ...updateData,
        consultantNotes: formdata.consultantNotestext
          ? { date: new Date(), consultantNote: formdata.consultantNotestext }
          : null,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.message || "Something went wrong",
      });
    }
  }
);

export const DeleteLead = createAsyncThunk(
  "Lead/DeleteLead",
  async ({ id, uid }, thunkAPI) => {
    try {
      const leadRef = doc(firestore, "lead", id);
      await deleteDoc(leadRef);

      await thunkAPI.dispatch(getLeads({ uid }));

      return { message: "Lead deleted successfully" };
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.message || "Failed to delete lead",
      });
    }
  }
);

//List of appointments
export const getAppointments = createAsyncThunk(
  "Lead/getAppointments",
  async ({ uid }, thunkAPI) => {
    try {
      let data = [];
      const q = query(
        collection(firestore, "appointment"),
        where("adminId", "==", uid)
      );

      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        // let timestamp = null;
        // let updatedAt = null;
        // if (doc.data().createdAt) {
        //     let createdAt = doc.data().createdAt
        //     timestamp = createdAt?.nanoseconds / 1000000 + createdAt?.seconds * 1000
        //     timestamp = moment.utc(timestamp)
        // }

        // if (doc.data().updatedAt) {
        //     updatedAt = doc.data().updatedAt
        //     updatedAt = updatedAt?.nanoseconds / 1000000 + updatedAt?.seconds * 1000
        //     updatedAt = moment.utc(updatedAt)
        // }

        data.push({
          ...doc.data(),
          id: doc.id,
          leadName: doc.id,
        });
      });

      return [...data];
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  }
);

//Posting appointments
export const AddAppointments = createAsyncThunk(
  "Lead/AddAppointments",
  async ({ formdata, uid }, thunkAPI) => {
    const { getState } = thunkAPI;

    try {
      const formattedClientName = getFormattedClientName(formdata.clientName);
      const timestamp = new Date();

      const clientQuery = query(
        collection(firestore, "client"),
        where("adminId", "==", uid)
      );
      const clientSnapshots = await getDocs(clientQuery);

      let existingClient = null;
      let maxCustomerId = 0;

      for (const docSnap of clientSnapshots.docs) {
        const data = docSnap.data();

        if (data.customerId && data.customerId > maxCustomerId) {
          maxCustomerId = data.customerId;
        }

        const nameMatch =
          data.name?.toLowerCase().trim() ===
          formattedClientName?.toLowerCase().trim();
        const phoneMatch = data.phoneNumber === formdata.phoneNumber;
        const emailMatch = data.email === formdata.email;

        const allMatch = nameMatch && phoneMatch && emailMatch;

        if (allMatch) {
          existingClient = { ...data, id: docSnap.id };
          break;
        }

        if (phoneMatch && (!nameMatch || !emailMatch)) {
          return thunkAPI.rejectWithValue({
            message: "Phone number already exists.",
          });
        }

        if (emailMatch && (!nameMatch || !phoneMatch)) {
          return thunkAPI.rejectWithValue({
            message: "Email already exists.",
          });
        }

        if (nameMatch && (!phoneMatch || !emailMatch)) {
          return thunkAPI.rejectWithValue({
            message: "Name already exists.",
          });
        }
      }

      const appointmentData = {
        clientName: formattedClientName,
        countryCode: formdata.countryCode,
        phoneNumber: String(formdata.phoneNumber),
        email: formdata.email,
        customerNotes: formdata.customerNotes,
        appointmentDate: new Date(formdata.appointmentDate),
        createdAt: timestamp,
        adminId: uid,
      };

      if (formdata.consultantNotestext) {
        appointmentData.consultantNotes = [
          { date: timestamp, consultantNote: formdata.consultantNotestext },
        ];
      }

      const appointmentRef = await addDoc(
        collection(firestore, "appointment"),
        appointmentData
      );

      if (!existingClient) {
        const clientRef = doc(firestore, "client", appointmentRef.id);
        const clientData = {
          adminId: uid,
          name: formattedClientName,
          countryCode: formdata.countryCode,
          phoneNumber: String(formdata.phoneNumber),
          email: formdata.email,
          customerId: maxCustomerId + 1,
        };
        await setDoc(clientRef, clientData);
      }

      const state = getState();
      const adminName = state.user.name;
      const logo = state.user.logo;

      await sendAppointmentConfirmationEmail({
        email: formdata.email,
        clientName: formdata.clientName,
        appointmentDate: formdata.appointmentDate,
        name: adminName,
        logo,
      });

      return {
        ...appointmentData,
        consultantNotes: formdata.consultantNotestext
          ? [
              {
                date: timestamp,
                consultantNote: formdata.consultantNotestext,
              },
            ]
          : [],
        id: appointmentRef.id,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

//Edit appointment
export const EditAppointment = createAsyncThunk(
  "Lead/EditAppointment",
  async ({ formdata, uid }, thunkAPI) => {
    try {
      const timestamp = new Date();
      const formattedClientName = getFormattedClientName(formdata.clientName);
      const clientCollection = collection(firestore, "client");

      // === Phone Conflict Check ===
      const phoneQuery = query(
        clientCollection,
        where("phoneNumber", "==", formdata.phoneNumber),
        where("adminId", "==", formdata.adminId)
      );
      const phoneSnapshot = await getDocs(phoneQuery);
      const phoneConflict = phoneSnapshot.docs.some(
        (doc) => doc.id !== formdata.clientId
      );
      if (phoneConflict) {
        return thunkAPI.rejectWithValue({
          message: "Client with this Phone Number already exists.",
        });
      }

      // === Email Conflict Check ===
      if (formdata.email?.trim()) {
        const emailQuery = query(
          clientCollection,
          where("email", "==", formdata.email),
          where("adminId", "==", formdata.adminId)
        );
        const emailSnapshot = await getDocs(emailQuery);
        const emailConflict = emailSnapshot.docs.some(
          (doc) => doc.id !== formdata.clientId
        );
        if (emailConflict) {
          return thunkAPI.rejectWithValue({
            message: "Client with this Email already exists.",
          });
        }
      }

      // === Prepare Appointment Update Payload ===
      const updatePayload = {
        clientName: formattedClientName,
        countryCode: formdata.countryCode,
        phoneNumber: String(formdata.phoneNumber),
        email: formdata.email,
        customerNotes: formdata.customerNotes,
        appointmentDate: new Date(formdata.appointmentDate),
        updatedAt: timestamp,
      };

      if (formdata.consultantNotestext) {
        updatePayload.consultantNotes = arrayUnion({
          date: timestamp,
          consultantNote: formdata.consultantNotestext,
        });
      }

      const appointmentRef = doc(firestore, "appointment", formdata.id);
      await updateDoc(appointmentRef, updatePayload);

      // === Update or Create Client ===
      const clientRef = doc(firestore, "client", formdata.clientId);
      const clientSnap = await getDoc(clientRef);

      const clientData = {
        name: formattedClientName,
        countryCode: formdata.countryCode,
        phoneNumber: String(formdata.phoneNumber),
        email: formdata.email,
        adminId: formdata.adminId,
        updatedAt: timestamp,
      };

      if (clientSnap.exists()) {
        await updateDoc(clientRef, clientData);
      } else {
        await setDoc(clientRef, {
          ...clientData,
          createdAt: timestamp,
        });
      }

      return {
        id: formdata.id,
        ...updatePayload,
        consultantNotes: formdata.consultantNotestext
          ? {
              date: timestamp,
              consultantNote: formdata.consultantNotestext,
            }
          : null,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message:
          error.message || "Something went wrong while editing appointment",
      });
    }
  }
);

// Delete appointment
export const DeleteAppointment = createAsyncThunk(
  "Lead/DeleteAppointment",
  async ({ id, uid }, thunkAPI) => {
    try {
      const appointmentRef = doc(firestore, "appointment", id);
      await deleteDoc(appointmentRef);

      await thunkAPI.dispatch(getAppointments({ uid }));

      return { message: "Appointment deleted successfully" };
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.message || "Failed to delete appointment",
      });
    }
  }
);

export const LeadSlice = createSlice({
  name: "Lead",
  initialState: {
    leads: [],
    appointments: [],
    isFetching: false,
    isSuccess: false,
    isError: false,
    message: null,
  },
  reducers: {
    clearState: (state) => {
      state.isError = false;
      state.isSuccess = false;
      state.isFetching = false;
      state.message = null;
      return state;
    },
  },
  extraReducers: {
    [getLeads.fulfilled]: (state, { payload }) => {
      state.leads = payload ?? [];
      state.isFetching = false;
    },
    [getLeads.pending]: (state) => {
      state.isFetching = true;
    },
    [getLeads.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Server Error";
    },
    [DeleteLead.pending]: (state) => {
      state.isFetching = true;
      state.isError = false;
      state.message = null;
    },
    [DeleteLead.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isSuccess = true;
      state.message = payload.message;
    },
    [DeleteLead.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Server Error";
    },
    [EditLead.fulfilled]: (state, { payload }) => {
      state.leads = state.leads.map((item) => {
        if (item.id === payload.id) {
          if (payload.consultantNotes) {
            if (item.consultantNotes) {
              item = {
                ...item,
                ...payload,
                consultantNotes: [
                  ...item.consultantNotes,
                  payload.consultantNotes,
                ],
              };
            } else {
              item = {
                ...item,
                ...payload,
                consultantNotes: [
                  item.consultantNotes,
                  payload.consultantNotes,
                ],
              };
            }
          } else {
            item = {
              ...item,
              ...payload,
              consultantNotes: item.consultantNotes,
            };
          }
        }
        return { ...item };
      });
      state.isSuccess = true;
      state.isFetching = false;
      state.message = "Lead updated successfully";
    },

    [EditLead.pending]: (state) => {
      state.isFetching = false;
    },
    [EditLead.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Server Error";
    },

    [AddLead.fulfilled]: (state, { payload }) => {
      state.leads = [payload, ...state.leads];
      state.isSuccess = true;
      state.isFetching = false;
      state.message = "Lead added successfully";
    },
    [AddLead.pending]: (state) => {
      state.isFetching = false;
    },
    [AddLead.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Server Error";
    },

    [getAppointments.fulfilled]: (state, { payload }) => {
      state.appointments = payload ?? [];
      state.isFetching = false;
    },
    [getAppointments.pending]: (state) => {
      state.isFetching = true;
    },
    [getAppointments.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Server Error";
    },

    [EditAppointment.fulfilled]: (state, { payload }) => {
      state.appointments = state.appointments.map((item) => {
        if (item.id === payload.id) {
          if (payload.consultantNotes) {
            if (item.consultantNotes) {
              item = {
                ...item,
                ...payload,
                consultantNotes: [
                  ...item.consultantNotes,
                  payload.consultantNotes,
                ],
              };
            } else {
              item = {
                ...item,
                ...payload,
                consultantNotes: [
                  item.consultantNotes,
                  payload.consultantNotes,
                ],
              };
            }
          } else {
            item = {
              ...item,
              ...payload,
              consultantNotes: item.consultantNotes,
            };
          }
        }
        return { ...item };
      });
      state.isSuccess = true;
      state.message = "Appointments updated successfully";
    },

    [EditAppointment.pending]: (state) => {
      state.isFetching = false;
    },
    [EditAppointment.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Server Error";
    },
    [DeleteAppointment.pending]: (state) => {
      state.isFetching = true;
      state.isError = false;
      state.message = null;
    },
    [DeleteAppointment.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isSuccess = true;
      state.message = payload.message;
    },
    [DeleteAppointment.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Server Error";
    },

    [AddAppointments.fulfilled]: (state, { payload }) => {
      state.appointments = [payload, ...state.appointments];
      state.isSuccess = true;
      state.isFetching = false;
      state.message = "Appointment added & email sent.";
    },
    [AddAppointments.pending]: (state) => {
      state.isFetching = false;
    },
    [AddAppointments.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Server Error";
    },
  },
});

export const { clearState } = LeadSlice.actions;

export const LeadSelector = (state) => state.lead;
